<script setup lang="ts">
import { onMounted, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { CRUISE } from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import { useBookComposable } from './useBookComposable';
import { pick } from 'lodash';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import { useModalComposable } from '@/views/composable';
import Accordion from 'primevue/accordion';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import AccordionPanel from 'primevue/accordionpanel';
import AutoComplete from 'primevue/autocomplete';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import TableColumn from 'primevue/column';

interface Props {
  hotel?: any;
  isMdt?: boolean;
  room?: any;
  detailedOccupancy?: any;
  qType?: string;
  isCabin?: boolean;
  cabin?: any;
  cabinDetail?: any;
  cabinCategory?: any;
  cabinType?: string;
}
const props = defineProps<Props>();

const emits = defineEmits<{ updateBookingRequestForm: [value: any] }>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const { handleOpenErrorMessage } = useModalComposable();
const { checkCabinCode } = useBookComposable();
const langCapitalize = capitalize(currentLanguage);
const occupancyExpandedRows = ref<any>({});
const occupancy = ref<any>([]);
const maxChildren = 4;
const activePanel = ref('0');
const maxInfants = 2;

const chooseField = (hotel: any) => {
  const fieldName = `tourName${langCapitalize}`;
  if (!props.isCabin) {
    return props.isMdt ? `${hotel[fieldName]}` : `${hotel.hotel}`;
  }
};

const getCabinsName = (cabinCode: any) => {
  const foundCabin = props.cabinCategory.find((cabin: any) => cabin.code === cabinCode);
  return foundCabin?.code && (foundCabin?.nameFr || foundCabin?.nameEn) ? `${getTextVariant(currentLanguage, foundCabin.nameEn, foundCabin.nameFr)} - ${foundCabin.code}` : checkCabinCode(cabinCode);
};

const chooseFieldRoomName = (room: any) => {
  if (!props.isCabin) {
    if (props.qType === CRUISE) {
      return getTextVariant(currentLanguage, room.longName, room.longNameFr);
    } else {
      return props.isMdt ? `${getTextVariant(currentLanguage, room.longName, room.longNameFr)}` : `${room.room}`;
    }
  } else {
    return getCabinsName(props.cabinType);
  }
};

const columns = [
  { label: t('book_form.single'), name: t('book_form.single'), field: 'single', peopleNo: 1 },
  { label: t('book_form.double'), name: t('book_form.double'), field: 'double', peopleNo: 2 },
  { label: t('book_form.triple'), name: t('book_form.triple'), field: 'triple', peopleNo: 3 },
  { label: t('book_form.quad'), name: t('book_form.quad'), field: 'quad', peopleNo: 4 },
  { label: t('book_form.five'), name: t('book_form.five'), field: 'fiveInRoom', peopleNo: 5 },
  { label: t('book_form.six'), name: t('book_form.six'), field: 'sixInRoom', peopleNo: 6 }
];

const fillChildren = (field: string | undefined): number => {
  const column = columns.find((col: any) => col.field === field);
  return column ? maxChildren - column.peopleNo : 0;
};

const isDisableChildrenCabin = (field: string) => {
  return props.isCabin && fillChildren(field) < 1;
};

const removeRoomSuffix = (data: any) => {
  const newData: any = {};
  for (const key in data) {
    const newKey = key.replace('Room', '');
    newData[newKey] = data[key];
  }
  return newData;
};

const updateDetailedOccupancy = (data: any) => {
  const detailedOccupancyClone = cloneDeep(props.detailedOccupancy);
  const detailedOccupancyTemp: any = removeRoomSuffix(detailedOccupancyClone);
  return data.map((item: any) => {
    if (detailedOccupancyTemp[item.field]) {
      item.detailedOccupancy.map((occupancy: any, index: number) => {
        if (detailedOccupancyTemp[item.field][index]) {
          item.detailedOccupancy[index] = detailedOccupancyTemp[item.field][index];
        }
        return occupancy;
      });
    }
    return item;
  });
};

const initOccupancy = () => {
  if (props.room?.roomInfo && !props.isCabin) {
    const roomInfo: any = props.room;
    columns.forEach((column: any) => {
      const numberPeople = pick(props.room, ['maxPaxAdult', 'maxPaxChild', 'maxPaxRoom', 'minAdPerChild', 'minPaxAdult', 'minPaxChild', 'minPaxRoom', 'minChild', 'maxChild']);
      const detailedOccupancy = [];
      if (roomInfo.roomInfo?.[`${column.field}No`]) {
        for (let i = 0; i < roomInfo.roomInfo?.[`${column.field}No`]; i++) {
          detailedOccupancy.push({ adults: column.peopleNo, children: null, ages: null, infants: null });
        }
        occupancy.value.push({ ...numberPeople, label: column.label, field: column.field, roomNo: roomInfo.roomInfo?.[`${column.field}No`], detailedOccupancy: detailedOccupancy });
      }
    });
  }
  if (props.isCabin) {
    columns.forEach((column) => {
      if (props.cabin[`${column.field}No`]) {
        const detailedOccupancy = [];
        for (let i = 0; i < props.cabin[`${column.field}No`]; i++) {
          detailedOccupancy.push({ adults: column.peopleNo, children: null, ages: null, infants: null });
        }
        occupancy.value.push({ label: column.label, field: column.field, roomNo: props.cabin[`${column.field}No`], detailedOccupancy: detailedOccupancy, minChild: props.cabinDetail?.minChild, maxChild: props.cabinDetail?.maxChild });
      }
    });
  }
  if (props.detailedOccupancy) {
    updateDetailedOccupancy(occupancy.value);
  }
};
initOccupancy();

const addRoomSuffix = (data: any) => {
  const newData: any = {};
  for (const key in data) {
    const newKey = key + 'Room';
    newData[newKey] = data[key];
  }
  return newData;
};

const validateInput = (event: any) => {
  const value = event.target.value;
  const regexNumber = /^\d+$/;

  if (!regexNumber.test(value)) {
    event.target.value = value.replace(/\D/g, '');
  }
};

const updateBookingRequestForm = () => {
  const occupancyConvert = transformOccupancyData(occupancy.value);
  const occupancyConvertSecond = addRoomSuffix(occupancyConvert);
  emits('updateBookingRequestForm', occupancyConvertSecond);
};

const transformOccupancyData = (data: any) => {
  const detailedOccupancy: any = {};
  data.forEach((item: any) => {
    const field = item.field;
    detailedOccupancy[field] = item.detailedOccupancy;
  });
  return detailedOccupancy;
};

const blurChildren = async (event: number, indexRoom: number, index: number, field: any, maxPaxChild: number, maxPaxRoom: number) => {
  if (event === null) {
    return;
  }

  const current = occupancy.value[indexRoom].detailedOccupancy[index];
  const numAdults = current.adults;
  const maxRoom = props.isCabin ? 4 : maxPaxRoom;

  let newChildren = event;
  const showChildLimitError = newChildren > maxPaxChild;
  if (showChildLimitError) {
    newChildren = maxPaxChild;
  }

  const totalPeople = numAdults + newChildren;
  const showRoomLimitError = totalPeople > maxRoom;
  if (showRoomLimitError) {
    newChildren = Math.max(0, maxRoom - numAdults);
    if (!props.isCabin) {
      newChildren = Math.min(newChildren, maxPaxChild);
    }
  }

  if (newChildren !== event) {
    current[field] = null;
    await nextTick();
    current[field] = newChildren;

    handleOpenErrorMessage(showRoomLimitError ? t('booking.max_people') : t('book_form.children_not_permitted'));
  }
};

const resetAges = (event: any, indexRoom: number, index: number, field: any) => {
  if (occupancy.value[indexRoom].detailedOccupancy[index][field] !== event.value) {
    occupancy.value[indexRoom].detailedOccupancy[index].ages = null;
    updateBookingRequestForm();
  }
};

const blurInfants = async (event: number, indexRoom: number, index: number, field: string) => {
  if (event !== null && event > maxInfants) {
    occupancy.value[indexRoom].detailedOccupancy[index][field] = null;
    await nextTick();
    occupancy.value[indexRoom].detailedOccupancy[index][field] = maxInfants;
    handleOpenErrorMessage(t('book_form.max_infants_room'));
  }
};

const convertAgesToNumbers = (data: any) => {
  return data.map((item: any) => {
    return Number(item);
  });
};

const updateAges = (field: string, indexRoom: number, index: number) => {
  if (occupancy.value[indexRoom].detailedOccupancy[index][field]) {
    occupancy.value[indexRoom].detailedOccupancy[index][field] = convertAgesToNumbers(occupancy.value[indexRoom].detailedOccupancy[index][field]);
    const agesArrLength = occupancy.value[indexRoom].detailedOccupancy[index][field].length;
    const newVal = occupancy.value[indexRoom].detailedOccupancy[index][field][agesArrLength - 1];
    const minChild = occupancy.value[indexRoom].minChild;
    const maxChild = occupancy.value[indexRoom].maxChild;

    if ((minChild && newVal < minChild) || (maxChild && newVal > maxChild)) {
      occupancy.value[indexRoom].detailedOccupancy[index][field].pop();
      document.activeElement instanceof HTMLElement && document.activeElement.blur();
      handleOpenErrorMessage(t('book_form.outside_age_range'));
    }

    if (occupancy.value[indexRoom].detailedOccupancy[index][field].length > occupancy.value[indexRoom].detailedOccupancy[index].children) {
      occupancy.value[indexRoom].detailedOccupancy[index][field].pop();
      document.activeElement instanceof HTMLElement && document.activeElement.blur();
      handleOpenErrorMessage(t('book_form.only_allow_children'));
    }
    if (occupancy.value[indexRoom].detailedOccupancy[index][field][occupancy.value[indexRoom].detailedOccupancy[index][field].length - 1] === 0) {
      occupancy.value[indexRoom].detailedOccupancy[index][field].pop();
      document.activeElement instanceof HTMLElement && document.activeElement.blur();
      handleOpenErrorMessage(t('book_form.age_greater_zero'));
    }
  }
  updateBookingRequestForm();
};

onMounted(() => {
  occupancy.value.forEach((item: any) => {
    occupancyExpandedRows.value = { ...occupancyExpandedRows.value, [item.field]: true };
  });
});

watch(
  () => occupancy.value,
  () => {
    updateBookingRequestForm();
  },
  { deep: true, immediate: true }
);

watch(
  () => props.room,
  () => {
    occupancy.value = [];
    initOccupancy();
  },
  { deep: true }
);

watch(
  () => props.cabin,
  () => {
    occupancy.value = [];
    initOccupancy();
  },
  { deep: true }
);
</script>

<template>
  <div v-if="occupancy && occupancy.length" class="detail-occupancy-form">
    <Accordion v-model:value="activePanel" class="acvShadow">
      <AccordionPanel value="0">
        <AccordionHeader>
          <b id="detailed-occupancy-title">
            {{ $t('book_form.detailed_occupancy').toUpperCase() }}: <span v-if="!isCabin">{{ chooseField(hotel) }} -</span> {{ chooseFieldRoomName(room) }}
          </b>
          <div class="absolute px-3 flex align-items-center bg-red-500 expand-icon">
            <i v-if="activePanel != 0" class="pi pi-plus-circle text-4xl text-white-alpha-90"></i>
            <i v-else class="pi pi-minus-circle text-4xl text-white-alpha-90"></i>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <div>{{ $t('book_form.note_select_number_of_children') }}</div>
          <DataTable tableClass="detail-occupancy-table" :value="occupancy" v-model:expandedRows="occupancyExpandedRows" dataKey="field" responsiveLayout="stack">
            <TableColumn :expander="true" class="w-3rem" />
            <TableColumn field="occ" class="room-column-expand">
              <template #body="slotProps">
                <b>{{ slotProps.data.label }}</b>
                {{ $t('book_form.occupancy_for') }}
                <b>{{ chooseField(hotel) }} - {{ chooseFieldRoomName(room) }}</b>
              </template>
            </TableColumn>
            <template #expansion="slotProps">
              <DataTable tableClass="room-info-table" :value="slotProps.data.detailedOccupancy" dataKey="field" responsiveLayout="stack">
                <TableColumn :header="$t('book_form.room')" class="room-column">
                  <template #body="slotProps">
                    <div>
                      {{ slotProps.index + 1 }}
                    </div>
                  </template>
                </TableColumn>
                <TableColumn field="adults" :header="$t('book_form.of_adults')" class="room-column">
                  <template #body="{ data, field }">
                    <InputNumber v-model="data[field]" :useGrouping="false" disabled />
                  </template>
                </TableColumn>
                <TableColumn field="children" :header="$t('book_form.of_children')" class="room-column">
                  <template #body="{ data, field, index }">
                    <InputNumber
                      v-model="data[field]"
                      :useGrouping="false"
                      :disabled="slotProps.data.maxPaxChild === 0 || isDisableChildrenCabin(slotProps.data.field)"
                      @update:modelValue="blurChildren($event, slotProps.index, index, field, slotProps.data.maxPaxChild, slotProps.data.maxPaxRoom)"
                      @input="resetAges($event, slotProps.index, index, field)"
                    />
                  </template>
                </TableColumn>
                <TableColumn field="ages" :header="$t('book_form.ages')" class="room-column ages-column">
                  <template #body="{ data, field, index }">
                    <AutoComplete :selectionLimit="200" v-model="data[field]" inputId="inputChip" multiple fluid :typeahead="false" @input="validateInput" @update:modelValue="updateAges(field, slotProps.index, index)" />
                  </template>
                </TableColumn>
                <TableColumn field="infants" :header="$t('book_form.of_infants')" class="room-column">
                  <template #body="{ data, field, index }">
                    <InputNumber v-model="data[field]" :useGrouping="false" @update:modelValue="blurInfants($event, slotProps.index, index, field)" />
                  </template>
                </TableColumn>
              </DataTable>
            </template>
          </DataTable>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>

<style scoped lang="scss">
.expand-icon {
  border-radius: 0 4px 4px 0;
  height: calc(100% - 6px);
  right: 2px;
}
.acvShadow :deep() {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  border: none !important;

  .p-accordionheader {
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #231f20;
  }

  .p-accordionheader:hover {
    background: white !important;
  }

  .p-accordion-toggle-icon {
    order: 2;
  }
}
:deep() {
  .p-datatable-table {
    width: 100%;

    &.room-info-table {
      .p-datatable-row-expansion > td {
        padding: 0;
      }
    }
    .room-column {
      width: 90px;
      min-width: 90px;
    }
    .room-column-expand {
      width: 95%;
    }
  }

  .p-datatable-row-expansion {
    background-color: $col-bluewt;
  }

  .detail-occupancy-table > .p-datatable-thead > tr > th {
    background: $col-white;
  }
}
</style>
